import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, RefreshCw } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { BOT_NAMES, INITIAL_TICKERS } from './adminPageDemoData';
import { resolveIsAdmin } from '../../utils/adminAccess';
import { useAdminLiveData } from './useAdminLiveData';
import {
  ADMIN_TRADE_LOGS_QUERY_LIMIT,
  buildAdminTradeMonthOptions,
} from '../../utils/adminTradeHistoryWindow';
import './AdminPage.css';

const VIEWS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'tickers', label: 'Ticker Universe' },
  { id: 'users', label: 'Users' },
  { id: 'logs', label: 'Trade Logs' },
  { id: 'echoprov', label: 'Echo Accounts' },
];

const IconDashboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 13h8V3H3zM13 21h8V11h-8zM13 3v6h8V3zM3 21h8v-6H3z" />
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5.8M21 20a5.5 5.5 0 0 0-4-5.3" />
  </svg>
);
const IconLogs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 6h12M8 12h12M8 18h12M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </svg>
);
const IconEcho = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.9 11a7 7 0 0 1 14.2 0" />
    <path d="M8 11.5a3.5 3.5 0 0 1 8 0" />
    <circle cx="12" cy="12" r="1.1" />
    <path d="M12 13v7" />
  </svg>
);
const IconBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);
const IconDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  </svg>
);
const IconChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16" />
  </svg>
);

const NAV_ICONS = {
  dashboard: IconDashboard,
  tickers: IconGlobe,
  users: IconUsers,
  logs: IconLogs,
  echoprov: IconEcho,
};

const statusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'active';
  if (s === 'trialing') return 'trialing';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  return 'active';
};

const statusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'trialing') return 'Trialing';
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  if (s === 'active') return 'Active';
  return status || '—';
};

const downloadCsv = (filename, headers, rows) => {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(',')].concat(
    rows.map((r) => headers.map((h) => escape(r[h])).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const subDateRef = (row) => row.renewal || row.created || null;

const monthOptions = buildAdminTradeMonthOptions();

const useAdminFonts = () => {
  useEffect(() => {
    const id = 'q-admin-fonts';
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
    return undefined;
  }, []);
};

const AdminPage = () => {
  useAdminFonts();
  const { user, userProfile, loading: authLoading, logout } = useAuthContext();
  const navigate = useNavigate();

  const [operatorClaim, setOperatorClaim] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setOperatorClaim(false);
      return undefined;
    }
    user
      .getIdTokenResult()
      .then((r) => {
        if (!cancelled) setOperatorClaim(r.claims?.qfaaOperator === true);
      })
      .catch(() => {
        if (!cancelled) setOperatorClaim(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const gateResolved = !authLoading && operatorClaim !== null;
  const isAdmin = resolveIsAdmin({ user, userProfile, operatorClaim });

  const live = useAdminLiveData(isAdmin, { canReadAllTrades: operatorClaim === true });

  const [view, setView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subSearch, setSubSearch] = useState('');
  const [subStatus, setSubStatus] = useState('ALL');
  const [subPeriod, setSubPeriod] = useState('ALL');
  const [subMonthKey, setSubMonthKey] = useState('ALL');
  const [subDate, setSubDate] = useState('');
  const [subSort, setSubSort] = useState('email-asc');
  const [userSearch, setUserSearch] = useState('');
  const [logMonthKey, setLogMonthKey] = useState(monthOptions[0].key);
  const [tickers, setTickers] = useState(() =>
    Object.fromEntries(Object.entries(INITIAL_TICKERS).map(([k, v]) => [k, [...v]]))
  );
  const [curBot, setCurBot] = useState(null);
  const [newTicker, setNewTicker] = useState('');

  const [epForm, setEpForm] = useState({
    uid: '',
    productKey: 'echo-spyqqq',
    accountId: '',
    accessToken: '',
    note: '',
    isSandbox: true,
  });
  const [epMsg, setEpMsg] = useState('');

  const subMonthFilterOptions = useMemo(() => {
    const seen = new Map();
    live.subscriberStats.rows.forEach((r) => {
      const d = subDateRef(r);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (seen.has(key)) return;
      seen.set(key, {
        key,
        year: d.getFullYear(),
        month: d.getMonth(),
        label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      });
    });
    return Array.from(seen.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [live.subscriberStats.rows]);

  const filteredSubs = useMemo(() => {
    const q = subSearch.trim().toLowerCase();
    let rows = live.subscriberStats.rows;

    if (q) {
      rows = rows.filter((r) =>
        `${r.email} ${r.product} ${r.status}`.toLowerCase().includes(q)
      );
    }
    if (subStatus !== 'ALL') {
      rows = rows.filter((r) => String(r.status || '').toLowerCase() === subStatus);
    }
    if (subPeriod !== 'ALL') {
      rows = rows.filter((r) => r.period === subPeriod);
    }
    if (subMonthKey !== 'ALL') {
      const [y, m] = subMonthKey.split('-').map(Number);
      rows = rows.filter((r) => {
        const d = subDateRef(r);
        return d && d.getFullYear() === y && d.getMonth() === m;
      });
    }
    if (subDate) {
      const [y, m, day] = subDate.split('-').map(Number);
      rows = rows.filter((r) => {
        const d = subDateRef(r);
        return (
          d &&
          d.getFullYear() === y &&
          d.getMonth() === m - 1 &&
          d.getDate() === day
        );
      });
    }

    const sorted = [...rows];
    if (subSort === 'email-asc') {
      sorted.sort((a, b) =>
        String(a.email || '').localeCompare(String(b.email || ''), undefined, {
          sensitivity: 'base',
        })
      );
    } else if (subSort === 'email-desc') {
      sorted.sort((a, b) =>
        String(b.email || '').localeCompare(String(a.email || ''), undefined, {
          sensitivity: 'base',
        })
      );
    } else if (subSort === 'renewal-asc') {
      sorted.sort(
        (a, b) => (subDateRef(a)?.getTime() || 0) - (subDateRef(b)?.getTime() || 0)
      );
    } else if (subSort === 'renewal-desc') {
      sorted.sort(
        (a, b) => (subDateRef(b)?.getTime() || 0) - (subDateRef(a)?.getTime() || 0)
      );
    } else {
      sorted.sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0));
    }
    return sorted;
  }, [
    subSearch,
    subStatus,
    subPeriod,
    subMonthKey,
    subDate,
    subSort,
    live.subscriberStats.rows,
  ]);

  const clearSubFilters = () => {
    setSubSearch('');
    setSubStatus('ALL');
    setSubPeriod('ALL');
    setSubMonthKey('ALL');
    setSubDate('');
    setSubSort('email-asc');
  };

  const toggleEmailSort = () => {
    setSubSort((prev) => (prev === 'email-asc' ? 'email-desc' : 'email-asc'));
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return live.users;
    return live.users.filter((r) =>
      `${r.email} ${r.name} ${r.role} ${r.status}`.toLowerCase().includes(q)
    );
  }, [userSearch, live.users]);

  const selectedLogMonth = monthOptions.find((m) => m.key === logMonthKey) || monthOptions[0];
  const tradeLogBundle = useMemo(
    () => live.buildTradeLogsForMonth(selectedLogMonth.year, selectedLogMonth.month),
    [live, selectedLogMonth.year, selectedLogMonth.month]
  );

  const userIdByEmail = useMemo(() => {
    const map = {};
    live.users.forEach((u) => {
      const email = String(u.email || '').trim().toLowerCase();
      if (email) map[email] = u.id;
    });
    return map;
  }, [live.users]);

  const crumb = VIEWS.find((v) => v.id === view)?.label || 'Dashboard';

  const selectView = (id) => {
    setView(id);
    setSidebarOpen(false);
    if (id !== 'tickers') setCurBot(null);
  };

  const addTicker = () => {
    if (!curBot) return;
    const t = newTicker.trim().toUpperCase();
    if (!t) return;
    setTickers((prev) => {
      if (prev[curBot].includes(t)) return prev;
      return { ...prev, [curBot]: [...prev[curBot], t] };
    });
    setNewTicker('');
  };

  const removeTicker = (symbol) => {
    if (!curBot) return;
    setTickers((prev) => ({
      ...prev,
      [curBot]: prev[curBot].filter((x) => x !== symbol),
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEchoProvision = (e) => {
    e.preventDefault();
    if (!epForm.uid.trim() || !epForm.accountId.trim() || !epForm.accessToken.trim()) {
      setEpMsg('uid, account #, and Tradier token are required.');
      return;
    }
    if (
      !epForm.isSandbox &&
      !window.confirm('This is a LIVE (real-money) account. Provision it for Echo?')
    ) {
      return;
    }
    setEpMsg('Demo — Echo provision API is not wired in this app yet.');
  };

  if (!gateResolved) {
    return (
      <div className="min-h-screen bg-[#0a1430] text-gray-300 flex items-center justify-center">
        Checking admin access…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a1430] p-8">
        <div className="bg-red-950/40 border border-red-500/40 rounded-lg p-6 max-w-xl">
          <div className="flex items-center gap-2 text-red-300">
            <Shield className="h-5 w-5" />
            <p>Access Denied: Admin privileges required</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`q-admin${sidebarOpen ? ' open' : ''}`}>
      <aside className="sidebar">
        <div className="side-top">
          <img src="/logojt.png" alt="JTNC" />
          <span className="side-title">Admin Dashboard</span>
        </div>
        <nav className="nav">
          {VIEWS.map((item) => {
            const Icon = NAV_ICONS[item.id];
            return (
              <button
                key={item.id}
                type="button"
                className={`nav-item${view === item.id ? ' active' : ''}`}
                onClick={() => selectView(item.id)}
              >
                <Icon /> {item.label}
              </button>
            );
          })}
        </nav>
        <div className="side-bottom">
          <Link className="nav-item" to="/dashboard" onClick={() => setSidebarOpen(false)}>
            <IconBack /> User Dashboard
          </Link>
          <Link className="nav-item" to="/admin/client-trade-history" onClick={() => setSidebarOpen(false)}>
            <IconLogs /> Live Trade History
          </Link>
          <button type="button" className="nav-item logout" onClick={handleLogout}>
            <IconLogout /> Logout
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-top">
          <button
            type="button"
            className="side-toggle"
            aria-label="Toggle menu"
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <IconMenu />
          </button>
          <span className="crumb">{crumb}</span>
        </div>

        <div className="wrap">
          {view === 'dashboard' && (
            <section className="view">
              <h1 className="page">Dashboard</h1>
              <p className="demo-note">
                Live from Firestore <code>tbl_stripe_payment_history</code>
                {live.lastUpdatedAt ? ` · updated ${live.lastUpdatedAt.toLocaleTimeString()}` : ''}
                {live.paymentsError ? ` · error: ${live.paymentsError}` : ''}
              </p>
              <div className="stats c4">
                <div className="stat"><div className="lab">Total Active Subscribers</div><b>{live.subscriberStats.active}</b></div>
                <div className="stat"><div className="lab">Active Monthly Subscribers</div><b>{live.subscriberStats.monthlyActive}</b></div>
                <div className="stat"><div className="lab">Active Annual Subscribers</div><b>{live.subscriberStats.annualActive}</b></div>
                <div className="stat"><div className="lab">Cancelling Subscribers</div><b>{live.subscriberStats.cancelling}</b></div>
              </div>
              <div className="stats c4">
                <div className="stat"><div className="lab">Annual Recurring Revenue</div><b className="cy">{live.formatMoney(live.subscriberStats.arr)}</b></div>
                <div className="stat"><div className="lab">Monthly Recurring Revenue</div><b className="cy">{live.formatMoney(live.subscriberStats.mrr)}</b></div>
                <div className="stat"><div className="lab">Monthly Recurring Revenue From Monthly Subscriptions</div><b className="cy">{live.formatMoney(live.subscriberStats.mrrMonthly)}</b></div>
                <div className="stat"><div className="lab">Monthly Recurring Revenue From Annual Subscriptions</div><b className="cy">{live.formatMoney(live.subscriberStats.mrrAnnual)}</b></div>
              </div>
              <div className="stats c4">
                <div className="stat" title="One-time QFAA deposits ($2,000) — not counted in Monthly / Annual Recurring Revenue or subscribers">
                  <div className="lab">QFAA Deposits Paid</div>
                  <b className="cy">{live.formatMoney(live.paymentStats.deposit)}</b>
                  <div className="lab">
                    {live.paymentStats.depositClients} clients
                    {live.paymentStats.firstDeposit ? ` · since ${live.formatDate(`${live.paymentStats.firstDeposit}T12:00:00`)}` : ''}
                  </div>
                </div>
                <div
                  className="stat"
                  title="Pay As You Go and Monthly Maintenance Fee payments. Kajabi monthly plans and the Maintenance Fee are recorded for the first charge only, so later months are missing."
                >
                  <div className="lab">Monthly Payments</div>
                  <b className="cy">{live.formatMoney(live.paymentStats.monthly)}</b>
                  <div className="lab">
                    {live.paymentStats.monthlyClients} clients
                    {live.paymentStats.renewalsMissing ? ` · ${live.paymentStats.renewalsMissing} missing later renewals` : ''}
                  </div>
                </div>
                <div className="stat"><div className="lab">Annual / One-time Plans</div><b className="cy">{live.formatMoney(live.paymentStats.other)}</b></div>
                <div className="stat" title="Test, trial, $1 affiliate and $0 rows left out; Echo is not included">
                  <div className="lab">Total Paid to JTNC</div>
                  <b className="cy">{live.formatMoney(live.paymentStats.total)}</b>
                  <div className="lab">{live.paymentStats.rows.length} clients</div>
                </div>
              </div>
              <div className="toolbar">
                <div className="search">
                  <IconSearch />
                  <input
                    placeholder="Search by email..."
                    value={subSearch}
                    onChange={(e) => setSubSearch(e.target.value)}
                  />
                </div>
                <select
                  className="sel"
                  aria-label="Filter by status"
                  value={subStatus}
                  onChange={(e) => setSubStatus(e.target.value)}
                >
                  <option value="ALL">All statuses</option>
                  <option value="active">Active</option>
                  <option value="trialing">Trialing</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  className="sel"
                  aria-label="Filter by period"
                  value={subPeriod}
                  onChange={(e) => setSubPeriod(e.target.value)}
                >
                  <option value="ALL">All periods</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
                <select
                  className="sel"
                  aria-label="Filter by month"
                  value={subMonthKey}
                  onChange={(e) => setSubMonthKey(e.target.value)}
                >
                  <option value="ALL">All months</option>
                  {subMonthFilterOptions.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
                <input
                  className="date-inp"
                  type="date"
                  aria-label="Filter by renewal date"
                  value={subDate}
                  onChange={(e) => setSubDate(e.target.value)}
                />
                <select
                  className="sel"
                  aria-label="Sort subscribers"
                  value={subSort}
                  onChange={(e) => setSubSort(e.target.value)}
                >
                  <option value="email-asc">Email A → Z</option>
                  <option value="email-desc">Email Z → A</option>
                  <option value="renewal-asc">Renewal soonest</option>
                  <option value="renewal-desc">Renewal latest</option>
                  <option value="recent">Newest payment</option>
                </select>
                <button type="button" className="btn ghost" onClick={clearSubFilters}>
                  Clear
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    downloadCsv(
                      'subscribers.csv',
                      ['email', 'product', 'lastPayment', 'period', 'status', 'renewalLabel'],
                      filteredSubs
                    )
                  }
                >
                  <IconDownload /> Export to CSV
                </button>
              </div>
              <p className="filter-meta">
                Showing {filteredSubs.length.toLocaleString()} of{' '}
                {live.subscriberStats.rows.length.toLocaleString()} subscribers
              </p>
              <div className="tbl-card">
                <div className="tbl-wrap">
                  {live.paymentsLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                      <RefreshCw className="h-5 w-5 animate-spin inline-block mr-2" /> Loading payments…
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <button
                              type="button"
                              className="th-sort"
                              onClick={toggleEmailSort}
                              title="Sort by email"
                            >
                              Email
                              <span className="th-sort-ind">
                                {subSort === 'email-asc' ? ' ↑' : subSort === 'email-desc' ? ' ↓' : ''}
                              </span>
                            </button>
                          </th>
                          <th>Product</th>
                          <th>Last Payment</th>
                          <th>Period</th>
                          <th>Status</th>
                          <th>
                            <button
                              type="button"
                              className="th-sort"
                              onClick={() =>
                                setSubSort((prev) =>
                                  prev === 'renewal-asc' ? 'renewal-desc' : 'renewal-asc'
                                )
                              }
                              title="Sort by renewal date"
                            >
                              Renewal Date
                              <span className="th-sort-ind">
                                {subSort === 'renewal-asc' ? ' ↑' : subSort === 'renewal-desc' ? ' ↓' : ''}
                              </span>
                            </button>
                          </th>
                          <th>Chart</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubs.length === 0 ? (
                          <tr>
                            <td colSpan={7} style={{ opacity: 0.6 }}>No payment records found</td>
                          </tr>
                        ) : (
                          filteredSubs.map((row) => {
                            const uid = userIdByEmail[String(row.email || '').trim().toLowerCase()];
                            return (
                              <tr key={row.id}>
                                <td className="email">{row.email || '—'}</td>
                                <td className="product-cell" title={row.product}>{row.product}</td>
                                <td>{row.lastPayment}</td>
                                <td>{row.period}</td>
                                <td>
                                  <span className={`badge ${statusClass(row.status)}`}>
                                    {statusLabel(row.status)}
                                  </span>
                                </td>
                                <td>{row.renewalLabel}</td>
                                <td>
                                  {uid ? (
                                    <Link
                                      className="btn ghost"
                                      style={{ padding: '6px 10px', fontSize: 12, textDecoration: 'none' }}
                                      to={`/admin/client-performance?userId=${encodeURIComponent(uid)}&email=${encodeURIComponent(row.email || '')}`}
                                    >
                                      Performance
                                    </Link>
                                  ) : (
                                    <span style={{ opacity: 0.45, fontSize: 12 }}>No user match</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="toolbar" style={{ marginTop: 24 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>Payments by Client</h2>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    downloadCsv(
                      'payments_by_client.csv',
                      ['email', 'deposit', 'depositDate', 'monthly', 'other', 'total', 'renewalsMissing'],
                      live.paymentStats.rows
                    )
                  }
                >
                  <IconDownload /> Export to CSV
                </button>
              </div>
              <p className="filter-meta">
                QFAA deposits and monthly / annual payments per client from <code>tbl_stripe_payment_history</code>.
                Test, trial, $1 affiliate and $0 rows left out; Echo not included.
              </p>
              <div className="tbl-card">
                <div className="tbl-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Deposit</th>
                        <th>Deposit Date</th>
                        <th>Monthly</th>
                        <th>Annual / One-time</th>
                        <th>Total Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {live.paymentStats.rows.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ opacity: 0.6 }}>No payment records found</td>
                        </tr>
                      ) : (
                        live.paymentStats.rows.map((r) => (
                          <tr key={r.email}>
                            <td className="email">{r.email}</td>
                            <td>{r.deposit ? live.formatMoney(r.deposit) : '—'}</td>
                            <td>{r.depositDate ? live.formatDate(`${r.depositDate}T12:00:00`) : '—'}</td>
                            <td title={r.renewalsMissing ? 'Later monthly renewals are not on record' : undefined}>
                              {r.monthly ? live.formatMoney(r.monthly) : '—'}
                              {r.renewalsMissing ? ' *' : ''}
                            </td>
                            <td>{r.other ? live.formatMoney(r.other) : '—'}</td>
                            <td><b>{live.formatMoney(r.total)}</b></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {live.paymentStats.renewalsMissing > 0 && (
                <p className="filter-meta">* Later monthly renewals are not on record (Kajabi plans and the Monthly Maintenance Fee store the first charge only).</p>
              )}
            </section>
          )}

          {view === 'tickers' && (
            <section className="view">
              <h1 className="page">Ticker Universe</h1>
              <p className="page-sub">Pick a bot to manage the tickers it is allowed to trade.</p>
              {!curBot ? (
                <div>
                  {Object.keys(BOT_NAMES).map((bot) => (
                    <button
                      key={bot}
                      type="button"
                      className="bot-row"
                      onClick={() => setCurBot(bot)}
                      style={{ width: '100%', color: 'inherit' }}
                    >
                      <span className="bot-ic">
                        <img src="/logojt.png" alt="" />
                      </span>
                      <span className="nm">{BOT_NAMES[bot]}</span>
                      <span className="chev"><IconChevron /></span>
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <button type="button" className="back-link" onClick={() => setCurBot(null)}>
                    <IconBack /> Back to bots
                  </button>
                  <h3 style={{ fontSize: 20, fontWeight: 700 }}>
                    {BOT_NAMES[curBot]} — ticker universe
                  </h3>
                  <div className="chips">
                    {tickers[curBot].map((t) => (
                      <span key={t} className="chip">
                        {t}
                        <button type="button" aria-label={`Remove ${t}`} onClick={() => removeTicker(t)}>
                          <IconX />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="add-ticker">
                    <input
                      value={newTicker}
                      onChange={(e) => setNewTicker(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addTicker();
                      }}
                      placeholder="Add ticker (e.g. NVDA)"
                      aria-label="Add ticker"
                    />
                    <button type="button" className="btn solid" onClick={addTicker}>
                      Add
                    </button>
                  </div>
                  <p className="demo-note" style={{ marginTop: 14 }}>
                    Local edits only — no shared ticker-universe collection in this project yet.
                  </p>
                </div>
              )}
            </section>
          )}

          {view === 'users' && (
            <section className="view">
              <h1 className="page">Users</h1>
              <p className="demo-note">
                Live from Firestore <code>users</code>
                {live.usersError ? ` · error: ${live.usersError}` : ''}
              </p>
              <div className="stats c3">
                <div className="stat"><div className="lab">Total Users</div><b>{live.userStats.total}</b></div>
                <div className="stat"><div className="lab">New Users (this month)</div><b>{live.userStats.newUsers}</b></div>
                <div className="stat"><div className="lab">Active Users</div><b>{live.userStats.active}</b></div>
              </div>
              <div className="toolbar">
                <div className="search">
                  <IconSearch />
                  <input
                    placeholder="Search by email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    downloadCsv(
                      'users.csv',
                      ['email', 'name', 'role', 'status', 'createdLabel'],
                      filteredUsers
                    )
                  }
                >
                  <IconDownload /> Export to CSV
                </button>
              </div>
              <div className="tbl-card">
                <div className="tbl-wrap">
                  {live.usersLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                      <RefreshCw className="h-5 w-5 animate-spin inline-block mr-2" /> Loading users…
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan={5} style={{ opacity: 0.6 }}>No users found</td>
                          </tr>
                        ) : (
                          filteredUsers.map((row) => (
                            <tr key={row.id}>
                              <td className="email">{row.email || '—'}</td>
                              <td>{row.name}</td>
                              <td className="role">{row.role}</td>
                              <td>
                                <span className={`badge ${statusClass(row.status)}`}>{row.status}</span>
                              </td>
                              <td>{row.createdLabel}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>
          )}

          {view === 'logs' && (
            <section className="view">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <h1 className="page">Monthly Trade Logs</h1>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Link className="btn ghost" to="/admin/client-trade-history" style={{ textDecoration: 'none' }}>
                    Open live history
                  </Link>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() =>
                      downloadCsv(
                        `trade-logs-${selectedLogMonth.key}.csv`,
                        ['accountId', 'email', 'count'],
                        tradeLogBundle.rows
                      )
                    }
                  >
                    <IconDownload /> Download CSV
                  </button>
                </div>
              </div>
              <p className="demo-note">
                Live aggregate from <code>tbl_user_order_backlogs</code> since October 2025
                (up to {ADMIN_TRADE_LOGS_QUERY_LIMIT.toLocaleString()} docs) joined to <code>users</code>
              </p>
              {live.tradesTruncated && (
                <div
                  style={{
                    margin: '0 0 16px',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    background: 'rgba(120, 53, 15, 0.25)',
                    color: '#fde68a',
                    fontSize: 13,
                    lineHeight: 1.45,
                  }}
                >
                  Trade log window hit the {ADMIN_TRADE_LOGS_QUERY_LIMIT.toLocaleString()}-row cap.
                  Monthly counts below may be incomplete for high-volume months.
                </div>
              )}
              {live.tradesError && (
                <div
                  style={{
                    margin: '0 0 16px',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid rgba(248, 113, 113, 0.35)',
                    background: 'rgba(127, 29, 29, 0.25)',
                    color: '#fecaca',
                    fontSize: 13,
                    lineHeight: 1.45,
                  }}
                >
                  {live.tradesError}
                </div>
              )}
              <div className="period">
                <select
                  className="sel"
                  aria-label="Month"
                  value={logMonthKey}
                  onChange={(e) => setLogMonthKey(e.target.value)}
                >
                  {monthOptions.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="stats c3">
                <div className="stat"><div className="lab">Connected Users</div><b>{tradeLogBundle.connectedUsers}</b></div>
                <div className="stat"><div className="lab">Users with Trades this Month</div><b>{tradeLogBundle.usersWithTrades}</b></div>
                <div className="stat"><div className="lab">Total Trades</div><b className="cy">{tradeLogBundle.totalTrades}</b></div>
              </div>
              <div className="tbl-card">
                <div className="tbl-wrap">
                  {live.tradesError ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                      Trade log rows unavailable until the operator claim is granted.
                    </div>
                  ) : live.tradesLoading || live.usersLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                      <RefreshCw className="h-5 w-5 animate-spin inline-block mr-2" /> Loading trades…
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Account ID</th>
                          <th>Email</th>
                          <th>Trade Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tradeLogBundle.rows.length === 0 ? (
                          <tr>
                            <td colSpan={3} style={{ opacity: 0.6 }}>No trades in {selectedLogMonth.label}</td>
                          </tr>
                        ) : (
                          tradeLogBundle.rows.map((row) => (
                            <tr key={`${row.accountId}-${row.userId}`}>
                              <td className="b">{row.accountId}</td>
                              <td className="email">{row.email}</td>
                              <td className="b">{row.count}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>
          )}

          {view === 'echoprov' && (
            <section className="view">
              <h1 className="page">Echo Accounts</h1>
              <p className="page-sub">
                Provision Tradier accounts onto Echo products. Form matches Quantum admin; API wiring is still demo here.
              </p>
              <div style={{ fontWeight: 700, fontSize: 13, margin: '0 0 18px', color: 'var(--amber)' }}>
                ● OMS status — not connected in this app
              </div>
              <form className="ep-form" onSubmit={handleEchoProvision}>
                <label className="field">
                  UID
                  <input
                    value={epForm.uid}
                    onChange={(e) => setEpForm((f) => ({ ...f, uid: e.target.value }))}
                    placeholder="echo-test-sbx-1"
                    autoComplete="off"
                  />
                </label>
                <label className="field">
                  Product
                  <select
                    value={epForm.productKey}
                    onChange={(e) => setEpForm((f) => ({ ...f, productKey: e.target.value }))}
                  >
                    <option value="echo-spyqqq">Echo · SPY &amp; QQQ</option>
                    <option value="echo-spx">Echo · SPX</option>
                  </select>
                </label>
                <label className="field">
                  Tradier account #
                  <input
                    value={epForm.accountId}
                    onChange={(e) => setEpForm((f) => ({ ...f, accountId: e.target.value }))}
                    placeholder="VA00000000"
                    autoComplete="off"
                  />
                </label>
                <label className="field">
                  Access token
                  <input
                    type="password"
                    value={epForm.accessToken}
                    onChange={(e) => setEpForm((f) => ({ ...f, accessToken: e.target.value }))}
                    placeholder="paste token"
                    autoComplete="off"
                  />
                </label>
                <label className="field span2">
                  Note
                  <input
                    value={epForm.note}
                    onChange={(e) => setEpForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. sandbox test account"
                    autoComplete="off"
                  />
                </label>
                <label className="field span2" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={epForm.isSandbox}
                    onChange={(e) => setEpForm((f) => ({ ...f, isSandbox: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: 'var(--cyan)' }}
                  />
                  <span>
                    Tradier <b>sandbox</b> account&nbsp;
                    <span style={{ color: 'var(--muted-dim)' }}>
                      (uncheck only for a LIVE / real-money account)
                    </span>
                  </span>
                </label>
                <div className="span2" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <button type="submit" className="btn solid">Provision</button>
                  <span style={{ fontSize: 13, color: epMsg.includes('Demo') || epMsg.includes('required') ? 'var(--red)' : 'var(--green-soft)' }}>
                    {epMsg}
                  </span>
                </div>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
